/**
 * GoalHistory — the History view on the Goals page (Story 18.7, AC #6).
 *
 * Mounted alongside the 18.8 active-goals table behind the page's Active /
 * History toggle (GoalsViewToggle flip button). Self-contained: runs its own
 * `useGoalHistory(clientId)` call (clientId optional — the page's client
 * filter scopes it server-side).
 *
 * Composition (all from the GET /api/goals/history payload):
 *   a. Past-goals table — name (+ platform logo when metric-linked, reusing
 *      the 18.8 row treatment), client, period label, final/target, and an
 *      ACHIEVED (green) / MISSED (red) badge in the 18.8 badge style.
 *      Server order is most-recent-first.
 *   b. Achievement-rate trend chart — Recharts bar chart of the monthly rate
 *      (Y = percentage, tooltip = achieved/total). Rendered ONLY when ≥2
 *      buckets exist; a single bucket shows a "Not enough history" hint.
 *   c. Metric-improvement strip — compact "Client — Metric: prev → latest
 *      (±%)" lines with green/red delta coloring (gray "—" when changePct is
 *      null). Hidden entirely when the array is empty.
 *   d. Empty state — zero past goals → a friendly empty card.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { formatGoalValue, formatGoalPeriodLabel } from "../../lib/goalFormat";
import {
  useGoalHistory,
  type PastGoal,
  type AchievementTrendBucket,
  type MetricImprovement,
} from "../../hooks/useGoalHistory";

interface GoalHistoryProps {
  /** Optional client scope (the page's client filter). */
  clientId?: string;
}

const NAVY = "#1e2a44";
const GREEN = "#10b981";
const RED = "#ef4444";

/**
 * Humanize a metric key for the improvement strip (same logic as the report
 * editor's MetricKeyCombobox.humanizeMetricKey — inlined here to keep the
 * History view from importing the heavy combobox component just for a label).
 */
function humanizeMetricKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function platformLogo(platform: string | null) {
  if (!platform) return null;
  const match = PICKER_PLATFORMS.find((p) => p.platform === platform);
  if (!match?.domain) return null;
  return (
    <img
      src={logoUrl(match.domain)}
      alt={match.displayName}
      title={match.displayName}
      className="h-4 w-4 rounded-sm object-contain"
      loading="lazy"
    />
  );
}

/** Outcome badge in the 18.8 status-badge style (ACHIEVED green / MISSED red). */
function OutcomeBadge({ outcome }: { outcome: PastGoal["outcome"] }) {
  if (outcome === "achieved") {
    return (
      <span
        className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
        data-testid="outcome-badge-achieved"
      >
        ACHIEVED
      </span>
    );
  }
  return (
    <span
      className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600"
      data-testid="outcome-badge-missed"
    >
      MISSED
    </span>
  );
}

// ── Trend chart tooltip: rate% with the raw achieved/total ──────────────────
function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: AchievementTrendBucket }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const bucket = payload[0]?.payload;
  if (!bucket) return null;
  return (
    <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
      <p className="mb-0.5 font-medium">{bucket.month}</p>
      <p className="text-muted-foreground">
        {Math.round(bucket.rate * 100)}% · {bucket.achieved}/{bucket.total} achieved
      </p>
    </div>
  );
}

function AchievementTrendChart({ trend }: { trend: AchievementTrendBucket[] }) {
  const data = trend.map((b) => ({ ...b, pct: Math.round(b.rate * 100) }));
  return (
    <div
      className="rounded-[22px] bg-muted/50 p-4 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
      data-testid="achievement-trend-chart"
    >
      <h3 className="mb-3 text-[13px] font-semibold text-foreground">
        Achievement rate over time
      </h3>
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
            <CartesianGrid stroke="hsl(214 25% 70% / 0.18)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: NAVY }}
              tickLine={false}
              axisLine={false}
              tickMargin={6}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 10, fill: NAVY }}
              tickLine={false}
              axisLine={false}
              width={40}
              tickMargin={4}
            />
            <Tooltip content={<TrendTooltip />} cursor={{ fill: "hsl(214 25% 70% / 0.12)" }} />
            <Bar dataKey="pct" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((b) => (
                <Cell key={b.month} fill={b.pct >= 50 ? GREEN : RED} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MetricImprovementStrip({ items }: { items: MetricImprovement[] }) {
  return (
    <div
      className="rounded-[22px] bg-muted/50 p-4 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
      data-testid="metric-improvement-strip"
    >
      <h3 className="mb-3 text-[13px] font-semibold text-foreground">
        Improvement across periods
      </h3>
      <ul className="space-y-2">
        {items.map((m) => {
          const positive = m.changePct !== null && m.changePct >= 0;
          const deltaColor =
            m.changePct === null
              ? "text-muted-foreground"
              : positive
                ? "text-emerald-600"
                : "text-red-600";
          const deltaLabel =
            m.changePct === null
              ? "—"
              : `${positive ? "+" : ""}${m.changePct.toFixed(1)}%`;
          return (
            <li
              key={`${m.clientId}-${m.metricKey}`}
              className="flex items-center gap-2 text-[13px]"
              data-testid={`improvement-${m.metricKey}`}
            >
              {platformLogo(m.platform)}
              <span className="font-medium text-foreground">
                {humanizeMetricKey(m.metricKey)}
              </span>
              <span className="text-muted-foreground tabular-nums">
                {m.previousValue.toLocaleString("en-US")} →{" "}
                {m.latestValue.toLocaleString("en-US")}
              </span>
              <span className={`font-semibold tabular-nums ${deltaColor}`}>
                ({deltaLabel})
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PastGoalsTable({ goals }: { goals: PastGoal[] }) {
  return (
    <div
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Past goals">
        <thead className="bg-background">
          <tr>
            <th scope="col" className="px-4 py-2 text-left text-[13px] font-semibold">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="goal" />
                Goal
              </span>
            </th>
            <th
              scope="col"
              className="hidden px-4 py-2 text-left text-[13px] font-semibold sm:table-cell whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="client" />
                Client
              </span>
            </th>
            <th
              scope="col"
              className="hidden px-4 py-2 text-left text-[13px] font-semibold md:table-cell whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="dateRange" />
                Period
              </span>
            </th>
            <th scope="col" className="px-4 py-2 text-left text-[13px] font-semibold whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="progress" />
                Final / Target
              </span>
            </th>
            <th scope="col" className="px-4 py-2 text-left text-[13px] font-semibold">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="outcome" />
                Outcome
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {goals.map((g) => (
            <tr
              key={g.id}
              className="bg-background"
              data-testid={`past-goal-row-${g.id}`}
            >
              <td className="px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  {platformLogo(g.platform)}
                  <span className="truncate font-medium" title={g.name}>
                    {g.name}
                  </span>
                </div>
              </td>
              <td className="hidden px-4 py-3 sm:table-cell">
                <span className="max-w-[160px] truncate text-[13px]" title={g.clientName}>
                  {g.clientName}
                </span>
              </td>
              <td className="hidden px-4 py-3 text-[13px] text-muted-foreground md:table-cell whitespace-nowrap">
                {formatGoalPeriodLabel(g.periodType, g.periodStart, g.periodEnd)}
              </td>
              <td className="px-4 py-3 text-[13px] tabular-nums whitespace-nowrap">
                {formatGoalValue(g.finalValue, g.unit, g.currencyCode)}
                <span className="text-muted-foreground">
                  {" / "}
                  {formatGoalValue(g.targetValue, g.unit, g.currencyCode)}
                </span>
              </td>
              <td className="px-4 py-3">
                <OutcomeBadge outcome={g.outcome} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GoalHistory({ clientId }: GoalHistoryProps) {
  const { data, isLoading, error, refetch } = useGoalHistory(clientId);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="goal-history-loading">
        <div
          className="h-48 rounded-[22px] bg-muted/60 animate-pulse"
          style={{ border: "0.5px solid var(--hairline)" }}
        />
        <div
          className="rounded-[22px] bg-muted/50 p-4 space-y-3"
          style={{ border: "0.5px solid var(--hairline)" }}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-muted/70 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-between rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
      >
        <span>{error.message}</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="cursor-pointer rounded-2xl px-3 py-1.5 text-[13px] shadow-xs transition-all hover:-translate-y-0.5 hover:bg-background hover:shadow-sm"
          style={{ border: "0.5px solid var(--hairline)" }}
        >
          Retry
        </button>
      </div>
    );
  }

  const pastGoals = data?.pastGoals ?? [];
  const trend = data?.achievementTrend ?? [];
  const improvement = data?.metricImprovement ?? [];

  // Empty state (AC #6d) — zero past goals → friendly card, never a blank pane.
  if (pastGoals.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center"
        data-testid="goal-history-empty"
      >
        <div className="mb-4 rounded-full bg-muted p-4">
          <ClockCounterClockwise size={32} className="text-muted-foreground" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">No goal history yet</h2>
        <p className="max-w-sm text-muted-foreground">
          Goal history appears here once a goal&apos;s period ends.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Trend chart — only with ≥2 buckets; otherwise a hint (AC #6b). */}
      {trend.length >= 2 ? (
        <AchievementTrendChart trend={trend} />
      ) : (
        <div
          className="rounded-[22px] bg-muted/40 px-4 py-3 text-[13px] text-muted-foreground"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="trend-insufficient"
        >
          Not enough history for a trend yet.
        </div>
      )}

      {/* Improvement strip — hidden entirely when empty (AC #6c). */}
      {improvement.length > 0 && <MetricImprovementStrip items={improvement} />}

      {/* Past-goals table (AC #6a). */}
      <PastGoalsTable goals={pastGoals} />
    </div>
  );
}
